import {ParsedUrlQuery} from "querystring";

import React from "react";
import {GetStaticProps, GetStaticPaths} from "next";
import {useRouter} from "next/router";
import {Global, css} from "@emotion/core";
import Head from "next/head";

import api from "~/tenant/api";
import ProductsScreen from "~/product/screens/Products";
import {ClientTenant} from "~/tenant/types";
import {Product} from "~/product/types";
import {Provider as I18nProvider} from "~/i18n/context";
import {Provider as CartProvider} from "~/cart/context";
import {Provider as AnalyticsProvider} from "~/analytics/context";
import {Provider as ProductProvider} from "~/product/context";
import {Provider as TenantProvider} from "~/tenant/context";
import {META} from "~/app/constants/config";
import {CURRENCIES} from "~/i18n/constants";

interface Props {
  tenant: ClientTenant;
  products: Product[];
}

interface Params extends ParsedUrlQuery {
  id: ClientTenant["id"];
}

const IdRoute: React.FC<Props> = ({tenant, products}) => {
  // Get router instance
  const router = useRouter();

  // Get the real product from the product id url
  const product = router.query.product
    ? products.find((product) => product.id === router.query.product) || null
    : null;

  return (
    <>
      <Global
        styles={css`
          html,
          body {
            height: 100%;
            width: 100%;
            max-width: 100vw;
          }

          * {
            letter-spacing: -0.2px;
            touch-action: manipulation;
            font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
              "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif !important;
          }
        `}
      />
      <Head>
        <link href={META.favicon} rel="icon" />
        <link href={tenant.logo || META.appleicon} rel="apple-touch-icon" />
        <link href={`${META.url}/${tenant.id}`} rel="canonical" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;500;700&display=swap"
          rel="stylesheet"
        />
        <title>{tenant.title || META.title}</title>
        <meta content={tenant.color || META.theme} name="theme-color" />
        <meta content={tenant.description || META.description} name="description" />
        <meta content={tenant.keywords || META.keywords} name="keywords" />
        <meta content={META.author} name="author" />
        <meta content={META.author} property="og:site_name" />
        <meta content={META.fbapp} property="fb:app_id" />
        <meta content="summary_large_image" name="twitter:card" />
        <meta
          content={tenant.twitter ? `@${tenant.twitter}` : META.twitter}
          name="twitter:creator"
        />
        <meta content={META.twitter} name="twitter:site" />
        {tenant.ga && (
          <>
            <script async src={`https://www.googletagmanager.com/gtag/js?id=${tenant.ga}`} />
            <script
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'UA-177635812-1');

                gtag('config', '${tenant.ga}');`,
              }}
            />
          </>
        )}
        {tenant.pixel && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `!function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '${tenant.pixel}');
                  fbq('track', 'PageView');`,
              }}
            />
            <noscript
              dangerouslySetInnerHTML={{
                __html: `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${tenant.pixel}&ev=PageView&noscript=1" />`,
              }}
            />
          </>
        )}
        {product ? (
          <>
            <meta content={product.title} name="twitter:title" />
            <meta content={product.description} name="twitter:description" />
            <meta content={product.images?.[0] || META.banner?.url} property="twitter:image" />
            <meta content={`${META.url}/${tenant.id}?product=${product.id}`} property="og:url" />
            <meta content="article" property="og:type" />
            <meta content={product.title} property="og:title" />
            <meta content={product.description} property="og:description" />
            <meta content={product.images?.[0]} property="og:image" />
            <meta content={product.images?.[0]} property="og:image:secure" />
            <meta content={product.images?.[0]} property="og:image:url" />
            <meta content="image/jpeg" property="og:image:type" />
            <meta content={product.title} property="og:image:alt" />
            <meta content={product.price.toFixed(2)} property="og:price:amount" />
            <meta content={CURRENCIES[tenant.country]} property="og:price:currency" />
            <meta content={product.price.toFixed(2)} property="product:price:amount" />
            <meta content={CURRENCIES[tenant.country]} property="product:price:currency" />
            <meta content={product.category} property="article:section" />
            <meta content={product.title} itemProp="name" />
            <meta content={product.description} itemProp="description" />
            <meta content={product.images?.[0]} itemProp="image" />
          </>
        ) : (
          <>
            <meta content={tenant.title || META.title} name="twitter:title" />
            <meta content={tenant.description || META.description} name="twitter:description" />
            <meta
              content={tenant.banner || tenant.logo || META.banner?.url}
              property="twitter:image"
            />
            <meta content={`${META.url}/${tenant.id}`} property="og:url" />
            <meta content="website" property="og:type" />
            <meta
              content={tenant.title ? `Pency | ${tenant.title}` : META.title}
              property="og:title"
            />
            <meta content={tenant.description || META.description} property="og:description" />
            <meta content={tenant.banner || tenant.logo || META.banner?.url} property="og:image" />
            <meta
              content={tenant.banner || tenant.logo || META.banner?.url}
              property="og:image:secure"
            />
            <meta
              content={tenant.banner || tenant.logo || META.banner?.url}
              property="og:image:url"
            />
            <meta content={META.banner?.format} property="og:image:type" />
            <meta content={META.banner?.width} property="og:image:width" />
            <meta content={META.banner?.height} property="og:image:height" />
            <meta content={tenant.title || META.title} property="og:image:alt" />
          </>
        )}
        <meta
          content="width=device-width, initial-scale=1.0, viewport-fit=contain"
          name="viewport"
        />
      </Head>
      <TenantProvider initialValue={tenant}>
        <ProductProvider initialValues={products}>
          <AnalyticsProvider>
            <CartProvider>
              <I18nProvider country={tenant.country}>
                <ProductsScreen />
              </I18nProvider>
            </CartProvider>
          </AnalyticsProvider>
        </ProductProvider>
      </TenantProvider>
    </>
  );
};

export const getStaticProps: GetStaticProps<any, Params> = async ({params: {id}}) => {
  try {
    // Get data
    const {products, ...tenant} = await api.fetch(id);

    // Get the revalidation time
    const revalidationTime = 10;

    // Return the props and revalidation times
    return {
      props: {tenant, products},
      revalidate: revalidationTime,
    };
  } catch (err) {
    return {
      // If something failed return a status code that will be intercepted by _app
      props: {
        statusCode: err?.status || err?.statusCode || 404,
      },
      // Revalidate after an hour
      revalidate: 3600,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    // Map slugs as params
    paths: [],
    // Build not relevant ones on demand
    fallback: "blocking",
  };
};

export default IdRoute;
